<script setup lang="ts">
import { ref } from 'vue'
import type { CallinglyCall } from '@/types/types'
import LoadingIcon from '@/components/LoadingIcon.vue'
import axios from '@/api'
import { useUserStore } from '@/stores/user'
import { storeToRefs } from 'pinia'

const userStore = useUserStore()
const { user } = storeToRefs(userStore)

const emit = defineEmits(['close', 'new-call'])

// State
const calls = ref<CallinglyCall[]>([])
const calls_loading = ref<boolean>(false)
const calls_page = ref<number>(1)

// Methods
const getCalls = (page = 1) => {
  if (page < 1) return

  calls_loading.value = true

  axios
    .get('/api/v1/calls?limit=5', {
      params: { page },
    })
    .then((response) => {
      calls.value = response.data.data
      calls_page.value = page
    })
    .finally(() => {
      calls_loading.value = false
    })
}

// Load initial data
getCalls()
</script>

<template>
  <i
    style="font-size: 30px; top: 80px; position: absolute; right: 10px; cursor: pointer"
    class="material-icons"
    @click="emit('close')"
    >close</i
  >
  <h3 class="text-center" style="margin-top: 0px; margin-bottom: 20px">Call History</h3>
  <table class="table table-striped table-hover" style="margin-bottom: 0; background-color: white">
    <tbody>
      <tr v-if="calls_loading">
        <td colspan="7">
          <p class="text-center"><LoadingIcon /></p>
        </td>
      </tr>
      <tr v-else-if="!calls.length">
        <td colspan="7">
          <p class="text-center">You don't have any calls!</p>
        </td>
      </tr>
      <tr v-else v-for="call in calls" :key="call.id" style="height: 72px">
        <td class="text-left" style="position: relative">
          <i class="material-icons" style="font-size: 16px; margin-top: 2px">{{
            call?.direction == 'outbound' ? 'call_made' : 'call_received'
          }}</i>
          <audio
            v-if="call?.recording_url"
            controls
            class="players"
            style="height: 29px; width: 143px; position: absolute; left: 10px; bottom: 8px"
          >
            <source :src="call?.recording_url" type="audio/mpeg" />
          </audio>
        </td>
        <td>
          {{ call.time_formatted }}
          <div v-if="call.lead_retry" style="font-size: 9px; padding-top: 3px" class="small-label">
            Followup #{{ call.lead_retry }}
          </div>
        </td>
        <td>
          <div v-if="call.status == 'missed'">Missed</div>
          <div v-else-if="call.status == 'busy'">Busy</div>
          <div v-else-if="call.status == 'offline'">Offline</div>
          <div v-else-if="call.status == 'in-progress'">In Progress</div>
          <div v-else-if="call.status == 'ringing'">Ringing</div>
          <div v-else-if="!call.status" />
          <div v-else-if="call.seconds == 0">No Answer</div>
          <div v-else>
            {{ call.duration }}
          </div>

          <div v-if="call.retry" style="font-size: 9px; padding-top: 3px" class="small-label">
            Retry #{{ call.retry }}
          </div>

          <div v-if="call.status == 'missed'">No agents answered</div>
          <div v-else-if="call.status == 'offline'">Outside availability hours</div>
          <div v-else-if="call.status == 'busy'">All agents busy</div>
          <div v-if="call.user">
            {{ call.user.name }}
          </div>
        </td>
        <td>
          <div v-if="user?.account_id == 6671 && call.lead">
            {{ call.lead.fname }} {{ call.lead.lname }}
          </div>
          <div v-else-if="call.lead">
            <a href="#" @click="emit('new-call', call.lead)">{{ call.lead.label }}</a
            ><br />
            <span v-if="call.lead.fname">{{ call.lead.phone_number_formatted }}</span>
          </div>
          <div v-else>
            {{ call.phone_number_formatted }}
          </div>
        </td>
      </tr>
    </tbody>
  </table>
  <ul
    class="pager"
    style="
      padding-left: 20px;
      padding-right: 20px;
      padding-top: 14px;
      position: absolute;
      right: 0;
      left: 0;
      bottom: 65px;
    "
  >
    <li :class="{ disabled: calls_page < 2 }" class="previous">
      <a style="border-radius: 4px" href="#" rel="previous" @click="getCalls(calls_page - 1)"
        >Previous</a
      >
    </li>
    <li class="next">
      <a style="border-radius: 4px" href="#" rel="next" @click="getCalls(calls_page + 1)">Next</a>
    </li>
  </ul>
</template>
<style scoped>
.pager {
  margin: 0;
}
</style>
